import React, { useEffect, useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import PageToolbar from './PageToolbar.js'
import redux from '../../redux/index.js'
import SearchField from '../SearchField.js'
import DeleteItem from '../dialogs/DeleteItem.js'
import { TablePagination } from '@mui/material'
import {
  Table,
  Link,
  styled,
  TD as Td
} from '@pergas-common/pergas-components'
import { Add, Email, Remove, OpenExternal } from '@pergas-common/pergas-icons'
import { createDateAccessor } from './columns.js'
import EditAccount from '../dialogs/EditAccount.js'
import { AccountIcon } from '../icons.js'

const TableHolder = styled.div`
  display: flex;
  position: absolute;
  top: 100px;
  bottom: 0;
  overflow-y: scroll;
`

const NameHolder = styled.div`
  display: inline-block;
  margin: 0 8px;
`

const Spacer = styled.div`
  display: inline-block;
  margin: 2px;
`

const ROWS_PER_PAGE = [
  25,
  50,
  100,
  500,
  1000
]

const AccountPage = ({
  locale,
  rows,
  limit,
  offset,
  orderBy,
  order,
  search,
  resetSearch,
  getPageItems,
  setOrder,
  setLimit,
  setOffset,
  setSearch,
  canUpdate,
  canDelete,
  deleteItem,
  onDeleteItemClick,
  onDeleteOk,
  onDeleteCancel,
  editItem,
  onEditItemClick,
  onEditOk,
  onEditCancel,
  canCreate,
  onShowAddDialog
}) => {
  useEffect(getPageItems, [limit, offset, orderBy, order, search])

  const manualSort = useCallback(({ id, isSorted, isSortedDesc }) => {
    if (isSorted && !isSortedDesc) {
      setOrder(id, 'desc')
    } else if (isSorted && isSortedDesc) {
      setOrder('', '')
    } else {
      setOrder(id, 'asc')
    }
  }, [setOrder])

  const columns = useMemo(() => {
    return [
      {
        Header: locale.name,
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          return (
            <Td
              {...cell.getCellProps()} left={
                <>
                  <AccountIcon style={{ width: 16, height: 16 }} />
                  <NameHolder>
                    {!!original.name && <span>{original.name}</span>}
                  </NameHolder>
                </>
            }
            />
          )
        },
        canSort: true,
        id: 'name',
        size: 'md',
        manualSort,
        sortType: () => {}
      },
      {
        id: 'permission',
        Header: locale.permission,
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          const result = Array.isArray(original.permissions) && original.permissions.map(r => locale[r.name]).sort().join(', ')
          return (<Td {...cell.getCellProps()} left={result && result} />)
        },
        isSortable: false
      },
      {
        Header: locale.tenant_name,
        manualSort,
        accessor: 'tenant_name',
        sortType: () => {}
      },
      {
        Header: locale.email,
        manualSort,
        accessor: 'email',
        sortType: () => {}
      },
      {
        id: 'updated_at',
        Header: locale.updated_at,
        manualSort,
        accessor: createDateAccessor('updated_at'),
        sortType: () => {}
      },
      {
        id: 'toolbar',
        Header: locale.tool_belt,
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          const { email } = original
          return (
            <Td
              {...cell.getCellProps()}
              left={
                <>
                  {email && <Spacer><Link href={`mailto:${email}`}><Email width={18} height={18} color='#28afe0' /></Link></Spacer>}
                  {canUpdate && <Spacer><Link onClickHandler={() => { onEditItemClick(original) }}><OpenExternal color='#28afe0' width={20} height={20} /></Link></Spacer>}
                </>
              }
              right={canDelete && <Link onClickHandler={() => { onDeleteItemClick(original) }}><Remove width={20} height={20} color='#FF5656' /></Link>}
            />
          )
        },
        size: 'sm',
        isSortable: false
      }
    ]
  }, [locale, canUpdate, canDelete, manualSort, onDeleteItemClick, onEditItemClick])

  let initialSortBy = []
  if (orderBy) {
    initialSortBy = [{
      id: orderBy,
      desc: order === 'desc'
    }]
  }

  return (
    <>
      {editItem && <EditAccount isEditing item={editItem} onOk={onEditOk} onCancel={onEditCancel} />}
      {deleteItem && <DeleteItem text={deleteItem.name || deleteItem.first_name} onOk={() => { onDeleteOk(deleteItem) }} onCancel={onDeleteCancel} />}
      <PageToolbar left={<><AccountIcon style={{ width: 20, height: 20 }} /><span>{locale.accounts}</span> {canCreate && <Link onClickHandler={onShowAddDialog}><Add width={20} height={20} color='#28afe0' /></Link>}</>}>
        <SearchField resetSearch={resetSearch} value={search} onChange={setSearch} />
      </PageToolbar>
      <TableHolder>
        <Table columns={columns} data={rows} initialPageSize={limit} initialSortBy={initialSortBy}>
          {({ setPageSize }) => (
            <TablePagination
              rowsPerPage={limit}
              rowsPerPageOptions={ROWS_PER_PAGE}
              count={-1}
              page={offset / limit}
              labelRowsPerPage={locale.rows_per_page}
              labelDisplayedRows={({ from, to }) => `${from}-${to}`}
              onRowsPerPageChange={(e) => {
                setLimit(e.target.value)
                setPageSize(e.target.value)
              }}
              onPageChange={(e, number) => setOffset(number * limit)}
            />
          )}
        </Table>
      </TableHolder>
    </>
  )
}

const mapStateToProps = (state) => {
  const { locale, account } = state
  const permissions = state.login.permissions
  const {
    pageItems,
    limit,
    offset,
    orderBy,
    order,
    search
  } = account
  return {
    locale: locale.strings,
    rows: pageItems,
    limit,
    offset,
    orderBy,
    order,
    search,
    canUpdate: permissions.account.canUpdate,
    canDelete: permissions.account.canDelete,
    canCreate: permissions.account.canCreate,
    deleteItem: state.account.deleteItem,
    editItem: state.account.editItem
  }
}

const mapDispatchToProps = (dispatch) => {
  const { actions: { account } } = redux
  return {
    onShowAddDialog: () => {
      dispatch(account.showAddAccountDialog())
    },

    onEditOk: (a) => {
      dispatch(account.hideEditAccountDialog())
      dispatch(account.updateAccount(a))
    },
    onEditItemClick: (a) => {
      dispatch(account.showEditAccountDialog(a))
    },
    onEditCancel: () => {
      dispatch(account.hideEditAccountDialog())
    },

    onDeleteOk: (a) => {
      dispatch(account.hideDeleteAccountDialog())
      dispatch(account.deleteAccount(a))
    },
    onDeleteItemClick: (a) => {
      dispatch(account.showDeleteAccountDialog(a))
    },
    onDeleteCancel: () => {
      dispatch(account.hideDeleteAccountDialog())
    },

    getPageItems: () => dispatch(account.getPageItems()),
    setOrder: (orderBy, order) => dispatch(account.setOrder(orderBy, order)),
    setLimit: (limit) => dispatch(account.setLimit(limit)),
    setOffset: (offset) => dispatch(account.setOffset(offset)),
    setSearch: (search) => dispatch(account.setSearch(search)),
    resetSearch: () => dispatch(account.resetSearch())
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AccountPage)
